import { Injectable } from '@angular/core';
import { CVApiService } from 'src/app/reusables/services/cv-api.service';
import { AgentContactActivity, ModuleData, SettingsDistinctStates, SingleAgentDetails, SingleAgentDetailsRSD, SingleAgentState } from './current-agent-states.models';

@Injectable({
    providedIn: 'root'
})
export class CurrentAgentStatesService {

    showDialog: false;
    moduleKey: "";
    groupID: "";
    dialogTargetAgent: "";
    selectedRow: any;

    constructor(
        private cvApiService: CVApiService
    ) { }

    // used in main componant
    async loadData(moduleKey: string, isAutoRefresh = false, isFirstLoad = false, abort: PromiseLike<void>,
        isCxOne: boolean, useMetrics: boolean){
        let renderEndpoint: string;
        try {
            renderEndpoint = await this.cvApiService.getModuleRenderEndPoint(moduleKey);
        } catch (error) {
            console.error("Failed getting module render endpoint");
            throw error;
        }
        try {
            const url = "/api/v2/dashboards/modules/currentagentstates/" + moduleKey + "?isAutoRefresh=" +
            !!isAutoRefresh + "&isFirstLoad=" + !!isFirstLoad + "&isCxOne=" + !!isCxOne + "&useMetrics=" + !!useMetrics;
            const response = await this.cvApiService.get<ModuleData>(url, renderEndpoint, abort);
            if (response.status_code >= 200 && response.status_code < 300) {
                return response.data;
            }
            else {
                throw response;
            }
        } catch (error) {
            console.error("Failed getting current agent state data");
            throw error;
        }
    }

    async loadContactData(moduleKey: string, agentID: number) {
        try {
            const url = "/api/v2/dashboards/modules/currentagentcontactactivity/" + moduleKey + "/agent/" + agentID;
            const response = await this.cvApiService.get<AgentContactActivity[]>(url);
            if (response.status_code >= 200 && response.status_code < 300) {
                return response.data;
            }
            else {
                throw response;
            }
        }
        catch (error) {
            console.error("Failed getting single agent contact activity data");
            throw error;
        }
    }


    // used in current agent states settings component
    async loadDistinctAgentStates(moduleKey: string) {
        try {
            const url = "/api/v2/agentstatedetails/distinctstates/" + moduleKey;
            const response = await this.cvApiService.get<SettingsDistinctStates>(url);
            if (response.status_code >= 200 && response.status_code < 300) {
                return response.data;
            }
            else {
                throw response;
            }
        } catch (error) {
            console.error("Failed getting current agent distinct states");
            throw error;
        }
    }

    // used in current agent states table component
    async loadCurrentAgentStatesByGroupID(moduleKey: string, groupID: number, isCXOne: boolean = false) {
        let renderEndpoint: string;
        try {
            renderEndpoint = await this.cvApiService.getModuleRenderEndPoint(moduleKey);
        } catch (error) {
            console.error("Failed getting module render endpoint");
            throw error;
        }
        try {
            const url = "/api/v2/dashboards/modules/currentagentstates/" + moduleKey + "/groupid/" + groupID + "/iscxone/" + isCXOne;
            const response = await this.cvApiService.get<SingleAgentState>(url, renderEndpoint);
            if (response.status_code >= 200 && response.status_code < 300) {
                return response.data;
            }
            else {
                throw response;
            }
        } catch (error) {
            console.error("Failed getting current agent state by group ID");
            throw error;
        }
    }

    // used in agent state detail component
    async getSingleAgentStates(moduleKey: string, groupID: number, userID: string = "", agentSessionID: string = "", givenDate: string, isCXOne = false) {

        try {
            const url = "/api/v2/modules/currentagentstates/" + moduleKey + "/groupid/" + groupID + "/givendate/" + givenDate + "/iscxone/" + isCXOne + "?userID=" + userID + "&agentsessionID=" + agentSessionID;
            let response;
            if (isCXOne)
                response = await this.cvApiService.get<SingleAgentDetailsRSD>(url);
            else
                response = await this.cvApiService.get<SingleAgentDetails>(url);

            if (response.status_code >= 200 && response.status_code < 300) {
                return response.data;
            }
            else {
                throw response;
            }
        } catch (error) {
            console.error("Failed getting single agent states");
            throw error;
        }
    }

    async getGroupTypeName(moduleKey: string) {
        try {
            const renderEndpoint = await this.cvApiService.getModuleRenderEndPoint(moduleKey);
            const url = '/api/v2/dashboards/modules/currentagentstates/grouptypename';
            const response = await this.cvApiService.get<string>(url, renderEndpoint);
            if (response.status_code >= 200 && response.status_code < 300) {
                return response.data;
            }
            else {
                throw response;
            }
        } catch (error) {
            console.error("Failed getting data to export evaluations and coaching module");
            throw error;
        }
    }

    public async getDataForExport(moduleKey: string, format: string, groupID: number, userID: string = "", agentSessionID: string = "", agentName: string, givenData: string, isCXOne: boolean = false){
        try {
            const renderEndpoint = await this.cvApiService.getModuleRenderEndPoint(moduleKey);
            const url = `/api/v2/dashboards/modules/currentagentstates/${encodeURIComponent(moduleKey)}/group/${groupID}/agentname/${agentName}/givendata/${givenData}/iscxone/${isCXOne}/export?format=${encodeURIComponent(format)}&userID=${userID}&agentsessionID=${agentSessionID}`;
            
            const response = await this.cvApiService.get<string>(url, renderEndpoint);
            if (response.status_code >= 200 && response.status_code < 300)
                return response;
            else throw response;
        } catch (err) {
            console.error("Failed getting data to export agent state details");
            throw err;
        }
    }

    /**
     * Downloads a file from a byte array string
     *
     * @param data The byte[] string returned from the endpoint
     * @param mime The mime type to mark the file as
     * @param fileName The name of the file to be downloaded
     * @memberof ModuleContainerComponent
     */
    downloadFile(data: string, mime: string, fileName: string) {
        const decodedData = atob(data);
        const buffer = new ArrayBuffer(decodedData.length);
        const view = new Uint8Array(buffer);
        for (let i = 0; i !== decodedData.length; ++i) {
            // eslint-disable-next-line no-bitwise
            view[i] = decodedData.charCodeAt(i) & 0xFF;
        }

        const fileData = [buffer];
        const blobObject = new Blob(fileData, { type: mime });

        // create fake link
        const linkElement = document.createElement('a');
        document.body.appendChild(linkElement);
        linkElement.setAttribute('style', 'display: none;');
        const url = URL.createObjectURL(blobObject);
        linkElement.href =  url;
        linkElement.download = fileName;

        // start download
        linkElement.click();

        // cleanup
        URL.revokeObjectURL(linkElement.href);
        linkElement.remove();
    };
}
